import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Loader2 } from "lucide-react";

export const ProtectedRoute = ({ children, requirePlatformAdmin = false }) => {
  const { isAuthenticated, loading, isPlatformAdmin, isCompanyAdmin, isAdminUser, user, canAccessAdminDashboard } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requirePlatformAdmin && !isPlatformAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Platform admin shouldn't access regular dashboard
  if (!requirePlatformAdmin && isPlatformAdmin) {
    return <Navigate to="/admin" replace />;
  }

  // Admin users (non-rota dashboard users) can access the admin dashboard
  if (isAdminUser) {
    return children;
  }

  // Staff users without admin dashboard access should go to staff dashboard
  // But staff with elevated roles (manager, accounts) can access the admin dashboard
  if (!requirePlatformAdmin && !isCompanyAdmin && user?.role === 'staff' && !canAccessAdminDashboard()) {
    return <Navigate to="/staff" replace />;
  }

  return children;
};

// Staff-specific route - only allows staff users (non-admins) OR staff who prefer the staff view
export const StaffRoute = ({ children }) => {
  const { isAuthenticated, loading, isPlatformAdmin, isCompanyAdmin, user, canAccessAdminDashboard } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Platform admin shouldn't access staff dashboard
  if (isPlatformAdmin) {
    return <Navigate to="/admin" replace />;
  }

  // Company admins should go to regular dashboard
  if (isCompanyAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Staff with elevated roles can still access staff dashboard if they choose
  // (this allows managers to view the staff portal to see what staff see)
  return children;
};

export const PublicRoute = ({ children, allowAuthenticated = false }) => {
  const { isAuthenticated, loading, isPlatformAdmin, isCompanyAdmin, user, canAccessAdminDashboard } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // If allowAuthenticated is true (e.g., for landing page), don't redirect
  if (allowAuthenticated) {
    return children;
  }

  // Redirect authenticated users to appropriate dashboard (for login/signup pages)
  if (isAuthenticated) {
    if (isPlatformAdmin) {
      return <Navigate to="/admin" replace />;
    }
    // Staff users with admin access go to dashboard, others to staff portal
    if (!isCompanyAdmin && user?.role !== 'super_admin') {
      // Check if they can access admin dashboard
      if (canAccessAdminDashboard()) {
        return <Navigate to="/dashboard" replace />;
      }
      return <Navigate to="/staff" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};
